import { booleanAttribute, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { AbstractComponent } from '@sol/components/components/abstract.component';
import { SolButtonType, SolText, TranslatePipe } from '@sol/components';
import { SolIcon } from '@sol/icons';
import { LabButtonComponent } from '../lab-button/lab-button.component';
import { LabIconComponent } from '../lab-icon/lab-icon.component';
import { LabNotification, LabNotificationMood } from './lab-notification.state';
import { LabAction } from '../../tools/lab-action';
import { coerceLabSolText, coerceOptionalLabSolText } from '../../tools/lab-sol-text-input';

export type { LabNotificationMood } from './lab-notification.state';

/**
 * Lab notes for Jolien — new notification card
 * ============================================
 * 1. This is named notification to match the existing SOL service, but the
 *    component is the toast card: compact, transient feedback.
 * 2. The visual card is split out from the plural notification host so the toast
 *    shape has a real component API instead of being hidden inside service markup.
 * 3. The public intent prop is `mood`, using the notification-relevant subset of
 *    the canonical mood vocabulary. Icons are intentionally derived from mood;
 *    consumers do not pick their own icon for feedback types.
 * 4. Text props use SolText. The workbench authors literal strings with
 *    SolText.ofRaw, while product consumers can pass translation keys.
 * 5. `action` and `secondaryAction` use the same LabAction shape as alert,
 *    banner, and empty-state, so feedback surfaces share one action API. The
 *    secondary action renders before the primary action to match platform button order.
 * 6. `dismissible` owns the lifecycle API. If the toast is not dismissible,
 *    it becomes timed and disappears on its own.
 * 7. Empty content is removed from the DOM. No blank heading/content shells.
 */

@Component({
  selector: 'lab-notification',
  standalone: true,
  imports: [LabButtonComponent, LabIconComponent, TranslatePipe],
  templateUrl: './lab-notification.component.html',
  styleUrl: './lab-notification.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class LabNotificationComponent extends AbstractComponent<LabNotification> {
  protected readonly SolButtonType = SolButtonType;
  protected readonly dismissIcon = SolIcon.remove;
  protected readonly dismissLabel = SolText.ofRaw('Dismiss notification');

  protected readonly hostClass$ = this.compute(state => state.hostClass);
  protected readonly heading$ = this.compute(state => state.heading);
  protected readonly description$ = this.compute(state => state.description);
  protected readonly mood$ = this.compute(state => state.mood);
  protected readonly icon$ = this.compute(state => state.icon);
  protected readonly iconMood$ = this.compute(state => state.iconMood);
  protected readonly actionButtonMood$ = this.compute(state => state.actionButtonMood);
  protected readonly action$ = this.compute(state => state.action);
  protected readonly secondaryAction$ = this.compute(state => state.secondaryAction);
  protected readonly dismissible$ = this.compute(state => state.dismissible);
  protected readonly timed$ = this.compute(state => state.timed);
  protected readonly hasIcon$ = this.compute(state => state.hasIcon);
  protected readonly hasHeading$ = this.compute(state => state.hasHeading);
  protected readonly hasDescription$ = this.compute(state => state.hasDescription);
  protected readonly hasContent$ = this.compute(state => state.hasContent);
  protected readonly hasAction$ = this.compute(state => state.hasAction);
  protected readonly hasControls$ = this.compute(state => state.hasControls);

  @Output('action')
  private readonly actionEmitter = new EventEmitter<LabAction>();

  @Output('secondaryAction')
  private readonly secondaryActionEmitter = new EventEmitter<LabAction>();

  @Output('dismiss')
  private readonly dismissEmitter = new EventEmitter<void>();

  constructor() {
    super(LabNotification.of());
  }

  @Input({ transform: coerceLabSolText })
  public set heading(heading: SolText) {
    this.update(state => state.withHeading(heading));
  }

  @Input({ transform: coerceOptionalLabSolText })
  public set description(description: SolText | undefined) {
    this.update(state => state.withDescription(description));
  }

  @Input()
  public set mood(mood: LabNotificationMood) {
    this.update(state => state.withMood(mood));
  }

  @Input()
  public set action(action: LabAction | undefined) {
    this.update(state => state.withAction(action));
  }

  @Input()
  public set secondaryAction(action: LabAction | undefined) {
    this.update(state => state.withSecondaryAction(action));
  }

  @Input({ transform: booleanAttribute })
  public set dismissible(dismissible: boolean) {
    this.update(state => state.withDismissible(dismissible));
  }

  protected onAction(action: LabAction): void {
    this.actionEmitter.emit(action);
  }

  protected onSecondaryAction(action: LabAction): void {
    this.secondaryActionEmitter.emit(action);
  }

  protected onDismiss(): void {
    this.dismissEmitter.emit();
  }
}
